<template>
  <Transition name="parent-modal-fade">
    <div v-if="isOpen" class="parent-modal-mask" @click="close">
      <section class="parent-modal" @click.stop>
        <button type="button" class="parent-modal-close" aria-label="关闭" @click="close">
          <font-awesome-icon icon="xmark" />
        </button>

        <header class="parent-modal-header">
          <h2 class="parent-modal-title">家长模式</h2>
          <p class="parent-modal-subtitle">请输入家长密码后继续</p>
        </header>

        <form class="parent-modal-body" @submit.prevent="submitPassword">
          <div class="parent-lookup-block">
            <label class="parent-lookup-label" for="parent-password-input">密码</label>
            <div class="parent-input-shell">
              <input
                id="parent-password-input"
                ref="passwordInputRef"
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                placeholder="请输入密码"
                class="parent-input"
                autocomplete="current-password"
                @input="passwordError = ''"
              />
              <button
                type="button"
                class="parent-input-side-btn"
                :aria-label="showPassword ? '隐藏密码' : '显示密码'"
                :title="showPassword ? '隐藏密码' : '显示密码'"
                @click="showPassword = !showPassword"
              >
                <font-awesome-icon :icon="showPassword ? 'eye-slash' : 'eye'" />
              </button>
            </div>
            <p v-if="passwordError" class="parent-lookup-error" role="alert">
              {{ passwordError }}
            </p>
          </div>

          <button
            type="submit"
            class="parent-submit-btn"
            :class="{ active: canSubmitPassword }"
            :disabled="!canSubmitPassword"
          >
            进入家长模式
          </button>
        </form>
      </section>
    </div>
  </Transition>
</template>

<script setup>
/* global defineEmits, defineExpose */
import { computed, nextTick, ref } from 'vue'
import { useRouter } from 'vue-router'
import { verifyPassword } from '@/api/user'

const PARENT_MODE_PASSWORD = '123456'
const PARENT_VERIFIED_STORAGE_KEY = 'tree-sprout-parent-verified'

const emit = defineEmits(['verified'])
const router = useRouter()

const isOpen = ref(false)
const password = ref('')
const passwordError = ref('')
const showPassword = ref(false)
const passwordInputRef = ref(null)

const canSubmitPassword = computed(() => password.value.trim().length > 0)

const resetForm = () => {
  password.value = ''
  passwordError.value = ''
  showPassword.value = false
}

const open = () => {
  resetForm()
  isOpen.value = true

  nextTick(() => {
    passwordInputRef.value?.focus()
  })
}

const close = () => {
  isOpen.value = false
  resetForm()
}

const submitPassword = async() => {
  if (!canSubmitPassword.value) {
    passwordError.value = '请输入密码'
    return
  }
  try{
    const rss = await verifyPassword(password.value)
    if(!rss) {
      passwordError.value = '密码不正确，请重新输入'
      return
    }
  } catch {
  }
 
  close()
  window.sessionStorage.setItem(PARENT_VERIFIED_STORAGE_KEY, '1')
  emit('verified')
  router.push('/parental')
}

defineExpose({
  open,
  close,
})
</script>

<style scoped>
.parent-modal-mask {
  position: fixed;
  inset: 0;
  z-index: 2200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(44, 39, 35, 0.34);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.parent-modal {
  position: relative;
  width: min(92vw, 420px);
  padding: 34px 32px 30px;
  border-radius: 28px;
  background: rgba(255, 253, 250, 0.96);
  border: 1px solid rgba(255, 255, 255, 0.88);
  box-shadow: 0 24px 60px rgba(98, 74, 55, 0.2);
}

.parent-modal-close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 138, 61, 0.1);
  color: #d67632;
  cursor: pointer;
  transition: transform 0.2s ease, background 0.2s ease;
}

.parent-modal-close:hover {
  transform: rotate(90deg);
  background: rgba(255, 138, 61, 0.18);
}

.parent-modal-header {
  text-align: center;
  margin-bottom: 24px;
}

.parent-modal-title {
  color: #2d3436;
  font-size: 26px;
  font-weight: 800;
}

.parent-modal-subtitle {
  margin-top: 8px;
  color: #9a8b82;
  font-size: 14px;
}

.parent-modal-body {
  display: grid;
  gap: 18px;
}

.parent-lookup-label {
  display: block;
  margin-bottom: 8px;
  color: #6b5c52;
  font-size: 13px;
  font-weight: 700;
}

.parent-input-shell {
  display: flex;
  align-items: center;
  min-height: 50px;
  padding: 0 10px 0 16px;
  border-radius: 16px;
  background: rgba(255, 246, 239, 0.92);
  border: 1px solid rgba(255, 164, 95, 0.22);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.78);
}

.parent-input {
  min-width: 0;
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  color: #2d3436;
  font-size: 15px;
  font-weight: 700;
}

.parent-input::placeholder {
  color: #bea99a;
  font-weight: 600;
}

.parent-input-side-btn {
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #d88a4b;
  cursor: pointer;
}

.parent-input-side-btn:hover {
  background: rgba(255, 138, 61, 0.1);
}

.parent-lookup-error {
  margin-top: 8px;
  color: #d65b4a;
  font-size: 12px;
  font-weight: 700;
}

.parent-submit-btn {
  width: 100%;
  min-height: 50px;
  border: none;
  border-radius: 999px;
  background: rgba(255, 138, 61, 0.18);
  color: rgba(171, 100, 51, 0.64);
  font-size: 16px;
  font-weight: 800;
  cursor: not-allowed;
  transition: transform 0.22s ease, box-shadow 0.22s ease, background 0.22s ease;
}

.parent-submit-btn.active {
  background: linear-gradient(135deg, #ff8a3d 0%, #ff6b35 100%);
  color: #ffffff;
  cursor: pointer;
  box-shadow: 0 12px 24px rgba(255, 119, 42, 0.25);
}

.parent-submit-btn.active:hover {
  transform: translateY(-1px);
}

.parent-modal-fade-enter-active,
.parent-modal-fade-leave-active {
  transition: opacity 0.22s ease;
}

.parent-modal-fade-enter-from,
.parent-modal-fade-leave-to {
  opacity: 0;
}

@media (max-width: 520px) {
  .parent-modal-mask {
    padding: 16px;
  }

  .parent-modal {
    padding: 30px 22px 24px;
    border-radius: 24px;
  }
}
</style>
